import { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Modal, ModalBody, ModalFooter, Spinner } from 'reactstrap';
import { useDropzone } from 'react-dropzone';
import FeatherIcon from 'feather-icons-react';
import Select from 'react-select';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import SignatureCanvas from 'react-signature-canvas';
import PlacesAutocomplete from 'react-places-autocomplete';
import { confirmIncidentUpload, getIncidentSignedUrls, requestIncidentUploadUrl, sendIncidentNotification } from './FormikHelpers/Api';
import { validateIncidentReport } from './FormikHelpers/ValidationSchema';
import { IncidentReportPreview } from './IncidentReportPreview';
import { IncidentReportHistory } from './IncidentReportHistory';
import { DownloadIncidentReportPDFButton } from './IncidentReportPDFTemplate';
import { Previewer } from '../../../../../components/DocumentPreviewer/Previewer';
import '../../Stylesheets/incident-report.scss';
import '../../Stylesheets/incident-report-public.scss';

const MAX_FILE_SIZE = 120 * 1024 * 1024;

const BLOCKED_EXTENSIONS = new Set([
  'exe', 'bat', 'cmd', 'com', 'scr', 'pif', 'msi', 'msp', 'cpl', 'dll', 'sys',
  'vbs', 'vbe', 'vb', 'js', 'jse', 'jar', 'wsf', 'wsh', 'ps1', 'psm1', 'sh',
  'htm', 'html', 'mhtml', 'xhtml', 'svg',
  'php', 'phtml', 'php3', 'php4', 'php5', 'phps',
  'asp', 'aspx', 'jsp', 'jspx', 'cgi', 'py', 'pyc', 'pyo', 'rb', 'pl',
  'reg', 'lnk', 'inf', 'apk', 'deb', 'rpm',
]);
const BLOCKED_MIME_TYPES = new Set([
  'text/html', 'application/xhtml+xml',
  'application/javascript', 'text/javascript', 'application/x-javascript',
  'image/svg+xml',
  'application/x-msdownload', 'application/x-msdos-program', 'application/x-msi',
  'application/x-sh', 'application/x-shellscript', 'application/x-bat',
  'application/java-archive', 'application/x-httpd-php', 'application/x-php', 'text/x-php',
  'application/vnd.microsoft.portable-executable',
]);
const isFileTypeAllowed = (fileName, mimeType) => {
  const ext = (typeof fileName === 'string' ? fileName.split('.').pop() : '').toLowerCase();
  if (ext && BLOCKED_EXTENSIONS.has(ext)) return false;
  if (mimeType && BLOCKED_MIME_TYPES.has(mimeType.toLowerCase())) return false;
  return true;
};

const selectMenuPortalStyles = { menuPortal: (base) => ({ ...base, zIndex: 9999 }) };

const strToOpt = (v) => ({ value: v, label: v });
const arrToOpts = (arr) => (arr || []).map(strToOpt);
const optsToArr = (opts) => (opts || []).map((o) => o.value);

const WEATHER_OPTIONS = ['Clear', 'Rain', 'Snow', 'Hail/Sleet', 'High Wind', 'Fog/Smog', 'Other', 'N/A'].map(strToOpt);
const ENVIRONMENT_OPTIONS = ['Dry', 'Wet', 'Muddy', 'Slush/Snow', 'Ice', 'Dusty', 'Hot', 'Cold', 'Indoor'].map(strToOpt);
const VISIBILITY_OPTIONS = ['Daylight', 'Sun Glare', 'Dark', 'Dim', 'Dusk', 'Artificial Light', 'N/A'].map(strToOpt);

const PERSON_TYPE_OPTIONS = [
  { value: 'employee', label: 'Employee' },
  { value: 'tenant', label: 'Tenant' },
  { value: 'contractor', label: 'Contractor' },
  { value: 'other', label: 'Other' },
];

const HOUR_OPTIONS = Array.from({ length: 12 }, (_, i) => ({ value: String(i + 1), label: String(i + 1) }));
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => ({ value: String(i).padStart(2, '0'), label: String(i).padStart(2, '0') }));
const AMPM_OPTIONS = [{ value: 'AM', label: 'AM' }, { value: 'PM', label: 'PM' }];

const dtpSelectStyles = (disabled) => ({
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  control: (base, state) => ({
    ...base,
    minHeight: 38, height: 38,
    borderRadius: 10,
    borderColor: state.isFocused ? '#163b5f' : '#dde3f0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(22,59,95,0.15)' : 'none',
    background: disabled ? '#f8fafc' : '#fafbff',
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    transition: 'all 0.2s',
    fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
  }),
  valueContainer: (base) => ({ ...base, padding: '0 10px', height: 38 }),
  singleValue: (base) => ({ ...base, color: '#061524', fontSize: 13, fontWeight: 500 }),
  placeholder: (base) => ({ ...base, color: '#bfc9d9', fontSize: 13 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: (base) => ({ ...base, padding: '0 6px', color: '#6a8ca8' }),
  menu: (base) => ({
    ...base, fontSize: 13, zIndex: 9999, borderRadius: 12,
    boxShadow: '0 12px 40px rgba(6,21,36,0.14)',
    border: '1.5px solid rgba(22,59,95,0.14)',
  }),
  option: (base, state) => ({
    ...base, padding: '8px 12px', borderRadius: 8,
    margin: '2px 4px', width: 'calc(100% - 8px)',
    background: state.isSelected
      ? 'linear-gradient(135deg,#163b5f,#3a6b98)'
      : state.isFocused ? 'rgba(22,59,95,0.07)' : '#fff',
    color: state.isSelected ? '#fff' : '#061524',
    cursor: 'pointer', fontWeight: state.isSelected ? 600 : 400,
  }),
  menuList: (base) => ({ ...base, maxHeight: 180, padding: 4 }),
});

const pirSelectStyles = {
  ...selectMenuPortalStyles,
  control: (base, state) => ({
    ...base,
    borderRadius: 10,
    borderColor: state.isFocused ? '#163b5f' : '#dde3f0',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(22,59,95,0.15)' : 'none',
    background: '#fafbff',
    minHeight: 38,
    fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
    fontSize: 13,
    transition: 'all 0.2s',
  }),
  placeholder: (base) => ({ ...base, color: '#bfc9d9', fontSize: 13 }),
  singleValue: (base) => ({ ...base, color: '#061524', fontSize: 13, fontWeight: 500 }),
  multiValue: (base) => ({ ...base, background: 'rgba(22,59,95,0.1)', borderRadius: 6 }),
  multiValueLabel: (base) => ({ ...base, color: '#163b5f', fontSize: 12, fontWeight: 600 }),
  multiValueRemove: (base) => ({ ...base, color: '#163b5f', ':hover': { background: 'rgba(22,59,95,0.2)', color: '#061524' } }),
  indicatorSeparator: () => ({ display: 'none' }),
  menu: (base) => ({
    ...base, zIndex: 9999, borderRadius: 12,
    boxShadow: '0 12px 40px rgba(6,21,36,0.14)',
    border: '1.5px solid rgba(22,59,95,0.14)',
    fontSize: 13,
  }),
  option: (base, state) => ({
    ...base, padding: '8px 12px', borderRadius: 8,
    margin: '2px 4px', width: 'calc(100% - 8px)',
    background: state.isSelected
      ? 'linear-gradient(135deg,#163b5f,#3a6b98)'
      : state.isFocused ? 'rgba(22,59,95,0.07)' : '#fff',
    color: state.isSelected ? '#fff' : '#061524',
    cursor: 'pointer', fontWeight: state.isSelected ? 600 : 400,
  }),
  menuList: (base) => ({ ...base, padding: 4 }),
};

const pad = (n) => String(n).padStart(2, '0');
const toLocalDate = (v) => v ? (v instanceof Date ? v : new Date(String(v))) : null;
const toLocalDateString = (v) => {
  const d = toLocalDate(v);
  if (!d || isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};
const todayLocalString = () => toLocalDateString(new Date());

export const isNotificationComplete = (values) => {
  const persons = values?.notification?.personsInvolved || [];
  if (!persons.length) return false;
  const notEmpty = (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
  return persons.every((p) => p && notEmpty(p.firstName) && notEmpty(p.lastName) && notEmpty(p.type));
};

export const isInvestigationComplete = (values) => {
  const inv = values?.investigation || {};
  const notEmpty = (v) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);
  return notEmpty(inv.occurrenceDateTime) && notEmpty(inv.statement);
};

const FL = ({ children, required, hint }) => (
  <label style={{
    display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 10.5, fontWeight: 700, color: '#3a6b98',
    textTransform: 'uppercase', letterSpacing: '0.5px',
    marginBottom: 5, cursor: 'default',
    fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
  }}>
    {children}
    {required && <span style={{ color: '#ef4444' }}> *</span>}
    {hint && <span style={{ fontSize: 10, color: '#6a8ca8', fontWeight: 400, textTransform: 'none' }}>({hint})</span>}
  </label>
);

const WitnessBlock = ({ w, idx, fieldPrefix, disabled, formik }) => (
  <div style={{ background: 'rgba(22,59,95,0.03)', border: '1.5px solid rgba(22,59,95,0.1)', borderRadius: 10, padding: '14px 16px', marginBottom: 8 }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10, marginBottom: 10 }}>
      <div>
        <FL required>First Name</FL>
        <PirInput placeholder="First name" value={w.firstName || ''} disabled={disabled}
          onChange={(e) => formik.setFieldValue(`${fieldPrefix}[${idx}].firstName`, e.target.value)} />
      </div>
      <div>
        <FL>Middle Name</FL>
        <PirInput placeholder="Middle name" value={w.middleName || ''} disabled={disabled}
          onChange={(e) => formik.setFieldValue(`${fieldPrefix}[${idx}].middleName`, e.target.value)} />
      </div>
      <div>
        <FL required>Last Name</FL>
        <PirInput placeholder="Last name" value={w.lastName || ''} disabled={disabled}
          onChange={(e) => formik.setFieldValue(`${fieldPrefix}[${idx}].lastName`, e.target.value)} />
      </div>
    </div>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
      <div style={{ gridColumn: 'span 1' }}>
        <FL required>Address</FL>
        <AddressInput placeholder="Address" value={w.address || ''} disabled={disabled}
          onChange={(val) => formik.setFieldValue(`${fieldPrefix}[${idx}].address`, val)} />
      </div>
      <div>
        <FL>Phone</FL>
        <PirInput placeholder="Phone number" value={w.phoneNo || ''} disabled={disabled}
          onChange={(e) => formik.setFieldValue(`${fieldPrefix}[${idx}].phoneNo`, e.target.value)} />
      </div>
      <div>
        <FL>Email</FL>
        <PirInput type="email" placeholder="email@example.com" value={w.email || ''} disabled={disabled}
          onChange={(e) => formik.setFieldValue(`${fieldPrefix}[${idx}].email`, e.target.value)} />
      </div>
    </div>
  </div>
);

const SectionCard = ({ number, title, subtitle, children }) => (
  <div className="pir-section-card">
    <div className="pir-section-header">
      <div className="pir-header-glow" />
      <div className="pir-section-number">{number}</div>
      <div style={{ position: 'relative', zIndex: 1 }}>
        <div className="pir-section-title" style={{ display: 'inline' }}>{title}</div>
        {subtitle && <span style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.65)', fontWeight: 400, marginLeft: 8 }}>— {subtitle}</span>}
      </div>
    </div>
    <div className="pir-section-body" style={{ position: 'relative', zIndex: 1 }}>{children}</div>
  </div>
);

const InvSubSection = ({ icon, label, children }) => (
  <div style={{ marginBottom: 20 }}>
    <div style={{
      display: 'flex', alignItems: 'center', gap: 8,
      marginBottom: 12, paddingBottom: 8,
      borderBottom: '1.5px solid rgba(22,59,95,0.1)',
    }}>
      <div style={{
        width: 24, height: 24, borderRadius: 7,
        background: 'linear-gradient(135deg,#163b5f,#3a6b98)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <FeatherIcon icon={icon} size={13} color="#fff" />
      </div>
      <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.6px', color: '#163b5f' }}>{label}</span>
    </div>
    {children}
  </div>
);

const WitnessGate = ({ choiceField, witnessesField, formik, editable, onAdd }) => {
  const getNested = (obj, path) => path.split('.').reduce((a, k) => {
    if (a == null) return undefined;
    const arrMatch = k.match(/^(\w+)\[(\d+)\]$/);
    if (arrMatch) return a[arrMatch[1]]?.[parseInt(arrMatch[2])];
    return a[k];
  }, obj);

  const witnessChoice = getNested(formik.values, choiceField);
  const witnesses = getNested(formik.values, witnessesField) || [];

  if (!editable) {
    if (witnesses.length === 0) return (
      <div style={{ textAlign: 'center', padding: '12px', color: '#6a8ca8', fontSize: 12, border: '1.5px dashed rgba(22,59,95,0.15)', borderRadius: 10, background: 'rgba(22,59,95,0.02)', marginBottom: 16 }}>
        No witnesses reported
      </div>
    );
    return null;
  }

  if (witnessChoice === 'yes' || witnesses.length > 0) return null;

  if (witnessChoice === 'no') {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: 'rgba(52,211,153,0.08)', border: '1.5px dashed rgba(52,211,153,0.4)', borderRadius: 12, marginBottom: 16 }}>
        <FeatherIcon icon="check-circle" size={14} color="#059669" />
        <span style={{ fontSize: 12.5, fontWeight: 600, color: '#065f46', flex: 1 }}>Confirmed: No witnesses to report</span>
        <BtnGhost style={{ fontSize: 11, padding: '3px 10px', background: '#fff' }} onClick={() => formik.setFieldValue(choiceField, null)}>Undo</BtnGhost>
      </div>
    );
  }

  return (
    <div style={{ textAlign: 'center', padding: '24px 20px', background: 'rgba(22,59,95,0.03)', border: '1.5px dashed rgba(22,59,95,0.2)', borderRadius: 14, marginBottom: 16 }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: 'linear-gradient(135deg,#163b5f,#3a6b98)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px', boxShadow: '0 4px 12px rgba(22,59,95,0.25)' }}>
        <FeatherIcon icon="users" size={20} color="#fff" />
      </div>
      <p style={{ fontSize: 14, fontWeight: 700, color: '#163b5f', margin: '0 0 4px' }}>Witnesses</p>
      <p style={{ fontSize: 12, color: '#6a8ca8', margin: '0 0 16px' }}>Were there any witnesses to this incident?</p>
      <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
        <BtnPrimary onClick={() => { formik.setFieldValue(choiceField, 'yes'); onAdd(); }}>
          <FeatherIcon icon="plus-circle" size={14} /> Yes, Add Witnesses
        </BtnPrimary>
        <BtnGhost onClick={() => formik.setFieldValue(choiceField, 'no')}>No, skip</BtnGhost>
      </div>
    </div>
  );
};

const PirInput = ({ style, ...props }) => (
  <input
    {...props}
    style={{
      width: '100%',
      border: '1.5px solid #dde3f0',
      borderRadius: 10,
      padding: '9px 13px',
      fontSize: 13.5,
      color: '#061524',
      background: props.disabled ? '#f8fafc' : '#fafbff',
      outline: 'none',
      fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
      transition: 'border-color 0.2s, box-shadow 0.2s, background 0.2s',
      cursor: props.disabled ? 'not-allowed' : undefined,
      ...style,
    }}
    onFocus={(e) => {
      if (!props.disabled) {
        e.target.style.borderColor = '#163b5f';
        e.target.style.boxShadow = '0 0 0 3px rgba(22,59,95,0.15)';
        e.target.style.background = '#fff';
      }
      props.onFocus?.(e);
    }}
    onBlur={(e) => {
      e.target.style.borderColor = '#dde3f0';
      e.target.style.boxShadow = 'none';
      e.target.style.background = props.disabled ? '#f8fafc' : '#fafbff';
      props.onBlur?.(e);
    }}
  />
);

const PirTextarea = ({ style, ...props }) => (
  <textarea
    {...props}
    style={{
      width: '100%',
      border: '1.5px solid #dde3f0',
      borderRadius: 10,
      padding: '9px 13px',
      fontSize: 13.5,
      color: '#061524',
      background: props.disabled ? '#f8fafc' : '#fafbff',
      outline: 'none',
      fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
      resize: 'vertical',
      lineHeight: 1.6,
      transition: 'border-color 0.2s, box-shadow 0.2s',
      ...style,
    }}
    onFocus={(e) => {
      if (!props.disabled) {
        e.target.style.borderColor = '#163b5f';
        e.target.style.boxShadow = '0 0 0 3px rgba(22,59,95,0.15)';
        e.target.style.background = '#fff';
      }
      props.onFocus?.(e);
    }}
    onBlur={(e) => {
      e.target.style.borderColor = '#dde3f0';
      e.target.style.boxShadow = 'none';
      e.target.style.background = props.disabled ? '#f8fafc' : '#fafbff';
      props.onBlur?.(e);
    }}
  />
);

const PirPill = ({ active, disabled, onClick, children }) => (
  <label
    onClick={!disabled ? onClick : undefined}
    style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 12px', borderRadius: 20, cursor: disabled ? 'not-allowed' : 'pointer',
      border: `1.5px solid ${active ? '#163b5f' : '#dde3f0'}`,
      background: active ? 'rgba(22,59,95,0.07)' : '#fff',
      color: active ? '#163b5f' : '#3a6b98',
      fontSize: 11.5, fontWeight: 600,
      boxShadow: active ? '0 0 0 2px rgba(22,59,95,0.12)' : 'none',
      transition: 'all 0.15s',
      userSelect: 'none', marginBottom: 0,
      opacity: disabled ? 0.55 : 1,
    }}
  >{children}</label>
);

const BtnPrimary = ({ children, style, ...props }) => (
  <button type="button" {...props} style={{
    display: 'inline-flex', alignItems: 'center', gap: 7,
    padding: '8px 16px',
    background: 'linear-gradient(135deg,#061524,#163b5f)',
    color: '#fff', border: 'none', borderRadius: 10,
    fontSize: 12.5, fontWeight: 700, cursor: props.disabled ? 'not-allowed' : 'pointer',
    fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
    boxShadow: '0 3px 12px rgba(22,59,95,0.35)',
    transition: 'all 0.2s',
    opacity: props.disabled ? 0.65 : 1,
    ...style,
  }}>{children}</button>
);

const BtnGhost = ({ children, style, ...props }) => (
  <button type="button" {...props} style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px',
    background: 'transparent', color: '#3a6b98',
    border: '1.5px solid rgba(22,59,95,0.2)',
    borderRadius: 10, fontSize: 12.5, fontWeight: 600,
    cursor: props.disabled ? 'not-allowed' : 'pointer',
    fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
    transition: 'all 0.15s',
    opacity: props.disabled ? 0.5 : 1,
    ...style,
  }}>{children}</button>
);

const BtnDangerSm = ({ children, ...props }) => (
  <button type="button" {...props} style={{
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '4px 10px',
    background: 'transparent', color: '#6a8ca8',
    border: '1.5px solid rgba(22,59,95,0.15)',
    borderRadius: 7, fontSize: 11.5, cursor: 'pointer',
    fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
    transition: 'all 0.15s',
  }}
    onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.borderColor = '#fca5a5'; e.currentTarget.style.color = '#ef4444'; }}
    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'rgba(22,59,95,0.15)'; e.currentTarget.style.color = '#6a8ca8'; }}
  >{children}</button>
);

const DateTimePicker = ({ value, onChange, disabled, maxNow = false }) => {
  const parseValue = (v) => {
    if (!v) return { date: '', hours: null, minutes: null, ampm: null };
    const d = toLocalDate(v);
    if (!d || isNaN(d.getTime())) return { date: '', hours: null, minutes: null, ampm: null };
    const h24 = d.getHours();
    const ap = h24 >= 12 ? 'PM' : 'AM';
    const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
    return {
      date: toLocalDateString(d),
      hours: { value: String(h12), label: String(h12) },
      minutes: { value: pad(d.getMinutes()), label: pad(d.getMinutes()) },
      ampm: { value: ap, label: ap },
    };
  };

  const normalise = (v) => {
    if (!v) return '';
    const d = toLocalDate(v);
    if (!d || isNaN(d.getTime())) return '';
    return `${toLocalDateString(d)}T${pad(d.getHours())}:${pad(d.getMinutes())}:00`;
  };

  const parsed = parseValue(value);
  const [localDate, setLocalDate] = useState(parsed.date);
  const [localHours, setLocalHours] = useState(parsed.hours);
  const [localMinutes, setLocalMinutes] = useState(parsed.minutes);
  const [localAmpm, setLocalAmpm] = useState(parsed.ampm);
  const prevNormRef = useRef(normalise(value));

  useEffect(() => {
    const norm = normalise(value);
    if (prevNormRef.current !== norm) {
      prevNormRef.current = norm;
      const p = parseValue(value);
      setLocalDate(p.date); setLocalHours(p.hours); setLocalMinutes(p.minutes); setLocalAmpm(p.ampm);
    }
  }, [value]);

  const buildAndEmit = (d, h, m, ap) => {
    if (!d || !h || !m || !ap) return;
    const [year, month, day] = d.split('-').map(Number);
    let h24 = parseInt(h, 10) % 12;
    if (ap === 'PM') h24 += 12;
    if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(h24)) return;
    const result = new Date(year, month - 1, day, h24, parseInt(m, 10), 0, 0);
    if (!isNaN(result.getTime())) onChange(result.toISOString());
  };

  const handleDate = (v) => { setLocalDate(v); buildAndEmit(v, localHours?.value, localMinutes?.value, localAmpm?.value); };
  const handleHours = (o) => { setLocalHours(o); buildAndEmit(localDate, o?.value, localMinutes?.value, localAmpm?.value); };
  const handleMinutes = (o) => { setLocalMinutes(o); buildAndEmit(localDate, localHours?.value, o?.value, localAmpm?.value); };
  const handleAmpm = (o) => { setLocalAmpm(o); buildAndEmit(localDate, localHours?.value, localMinutes?.value, o?.value); };

  return (
    <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
      <input
        type="date" value={localDate} disabled={disabled}
        max={maxNow ? todayLocalString() : undefined}
        onChange={(e) => handleDate(e.target.value)}
        style={{
          flex: '2 1 0', minWidth: 0,
          border: '1.5px solid #dde3f0', borderRadius: 10,
          padding: '9px 10px', fontSize: 13, color: '#061524',
          background: disabled ? '#f8fafc' : '#fafbff',
          outline: 'none', fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
          cursor: disabled ? 'not-allowed' : undefined,
        }}
      />
      {[
        [HOUR_OPTIONS, localHours, handleHours, 'HH'],
        [MINUTE_OPTIONS, localMinutes, handleMinutes, 'MM'],
        [AMPM_OPTIONS, localAmpm, handleAmpm, 'AM'],
      ].map(([opts, val, handler, ph]) => (
        <div key={ph} style={{ flex: '1 1 0', minWidth: 0 }}>
          <Select options={opts} value={val} isDisabled={disabled} onChange={handler}
            placeholder={ph} menuPortalTarget={document.body} menuPosition="fixed"
            styles={dtpSelectStyles(disabled)} isSearchable={false} />
        </div>
      ))}
    </div>
  );
};

const SignaturePad = ({ value, date, onChange, disabled, onDateChange }) => {
  const sigRef = useRef(null);
  const [sigModal, setSigModal] = useState(false);

  const handleClear = () => sigRef.current?.clear();

  const handleSave = () => {
    if (sigRef.current && !sigRef.current.isEmpty()) {
      const signatureData = sigRef.current.toDataURL('image/png');
      const today = todayLocalString();
      onDateChange?.(today);
      onChange(signatureData);
      setSigModal(false);
    } else {
      toast.warning('Please sign before saving.');
    }
  };

  const handleRemove = async () => {
    const result = await Swal.fire({
      title: 'Remove Signature?', icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#ef4444', cancelButtonColor: '#163b5f', confirmButtonText: 'Remove',
    });
    if (result.isConfirmed) { onChange(''); onDateChange?.(''); }
  };

  const formattedDate = date
    ? new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  return (
    <>
      {value ? (
        <div style={{ border: '1.5px solid rgba(22,59,95,0.2)', borderRadius: 12, padding: 12, background: 'linear-gradient(135deg,rgba(22,59,95,0.05),rgba(58,107,152,0.05))', display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ background: '#fff', borderRadius: 8, padding: 6, border: '1px solid rgba(22,59,95,0.15)' }}>
            <img src={value} alt="Signature" style={{ height: 44, maxWidth: 180, objectFit: 'contain' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: '#059669', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 4 }}>
              <FeatherIcon icon="check-circle" size={13} /> Signed
            </div>
            {formattedDate && <div style={{ fontSize: 11, color: '#163b5f', marginTop: 2, fontWeight: 500 }}>Signed on {formattedDate}</div>}
          </div>
          {!disabled && (
            <div style={{ display: 'flex', gap: 6 }}>
              <BtnGhost style={{ fontSize: 11, padding: '4px 10px' }} onClick={() => setSigModal(true)}>Re-sign</BtnGhost>
              <BtnDangerSm onClick={handleRemove}><FeatherIcon icon="trash-2" size={12} /></BtnDangerSm>
            </div>
          )}
        </div>
      ) : disabled ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', color: '#6a8ca8', fontSize: 12.5, background: 'rgba(22,59,95,0.03)', borderRadius: 9, border: '1px dashed rgba(22,59,95,0.15)' }}>
          <FeatherIcon icon="edit-3" size={13} color="#6a8ca8" />
          <span>No signature provided</span>
        </div>
      ) : (
        <button type="button" onClick={() => setSigModal(true)} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '13px 18px',
          background: 'linear-gradient(135deg,rgba(22,59,95,0.04),rgba(58,107,152,0.04))',
          border: '2px dashed rgba(22,59,95,0.25)', borderRadius: 12,
          cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#163b5f',
          fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
          transition: 'all 0.2s',
        }}>
          <FeatherIcon icon="edit-3" size={15} />
          <span>Click to Add Signature</span>
          <span style={{ marginLeft: 'auto', fontSize: 10, opacity: 0.6 }}>Required</span>
        </button>
      )}

      <Modal isOpen={sigModal} toggle={() => setSigModal(false)} centered size="lg" backdrop="static">
        <div style={{ padding: '20px 24px', borderBottom: '1.5px solid rgba(22,59,95,0.1)', background: 'linear-gradient(135deg,#061524,#163b5f)' }}>
          <h5 style={{ margin: 0, fontWeight: 700, color: '#fff', fontSize: 15 }}>✍️ Draw Your Signature</h5>
        </div>
        <ModalBody style={{ padding: 20 }}>
          <div style={{ border: '2px dashed rgba(22,59,95,0.25)', borderRadius: 12, background: 'linear-gradient(135deg,#fafbff,rgba(22,59,95,0.03))', position: 'relative' }}>
            <SignatureCanvas
              ref={sigRef} penColor="#061524"
              canvasProps={{ width: 560, height: 190, style: { width: '100%', height: 190 } }}
            />
            <div style={{ position: 'absolute', bottom: 10, left: 0, right: 0, textAlign: 'center', fontSize: 11, color: '#6a8ca8', pointerEvents: 'none', fontWeight: 500 }}>✦ Draw your signature in the area above ✦</div>
          </div>
          <p style={{ fontSize: 11, color: '#6a8ca8', marginTop: 10, textAlign: 'center' }}>By signing, you confirm all information provided is true and accurate.</p>
        </ModalBody>
        <ModalFooter style={{ border: 'none', paddingTop: 0, gap: 10 }}>
          <BtnGhost onClick={handleClear}><FeatherIcon icon="refresh-cw" size={13} /> Clear</BtnGhost>
          <BtnPrimary onClick={handleSave}><FeatherIcon icon="check" size={13} /> Save Signature</BtnPrimary>
        </ModalFooter>
      </Modal>
    </>
  );
};

const getFileExt = (k) => (typeof k === 'string' ? k.split('.').pop()?.toLowerCase() : '');
const fileNameFromKey = (k) => (typeof k === 'string' ? k.split('/').pop() : '');
const isVideoKey = (k) => /\.(mp4|mov|webm|avi|mkv)$/i.test(k);
const isPdfKey = (k) => /\.pdf$/i.test(k);
const isImageKey = (k) => /\.(jpe?g|png|gif|webp)$/i.test(k);

const FilePreviewModal = ({ isOpen, toggle, fileKey, signedUrl, name, onPrev, onNext }) => {
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      if (e.key === 'ArrowLeft' && onPrev) onPrev();
      if (e.key === 'ArrowRight' && onNext) onNext();
      if (e.key === 'Escape') toggle();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onPrev, onNext, toggle]);

  if (!isOpen || !signedUrl || !fileKey) return null;

  const ext = getFileExt(fileKey);

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = signedUrl;
    a.download = name || fileNameFromKey(fileKey);
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="xl" style={{ zIndex: 10060 }}>
      <div style={{
        padding: '14px 20px',
        background: 'linear-gradient(135deg,#061524,#163b5f)',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: 12,
      }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: '#fff', display: 'flex', alignItems: 'center', gap: 8, flex: 1, overflow: 'hidden' }}>
          {isVideoKey(fileKey) ? '🎬' : isPdfKey(fileKey) ? '📄' : isImageKey(fileKey) ? '🖼' : '📁'}{' '}
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name || fileNameFromKey(fileKey)}</span>
        </span>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={handleDownload}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '5px 12px',
              background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
              borderRadius: 8, color: '#fff', fontSize: 12, fontWeight: 600,
              cursor: 'pointer', transition: 'all 0.2s',
            }}
          >
            <FeatherIcon icon="download" size={13} /> Download
          </button>
          <button onClick={toggle} style={{ background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, color: '#fff', cursor: 'pointer', padding: '5px 8px', display: 'flex', alignItems: 'center' }}>
            <FeatherIcon icon="x" size={18} />
          </button>
        </div>
      </div>
      <ModalBody style={{ padding: 0, background: '#000', minHeight: 300, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
        {onPrev && (
          <button onClick={onPrev} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <FeatherIcon icon="chevron-left" size={20} />
          </button>
        )}
        {onNext && (
          <button onClick={onNext} style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 10, background: 'rgba(0,0,0,0.55)', color: '#fff', border: 'none', borderRadius: '50%', width: 44, height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
            <FeatherIcon icon="chevron-right" size={20} />
          </button>
        )}
        <div style={{ width: '100%', padding: '16px 56px' }}>
          <Previewer signedUrl={signedUrl} type={ext} url={fileKey} height="70vh" width="100%" />
        </div>
      </ModalBody>
      <div style={{ padding: '12px 20px', display: 'flex', justifyContent: 'center', gap: 10, borderTop: '1.5px solid rgba(22,59,95,0.1)', background: '#fff' }}>
        <a href={signedUrl} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'rgba(22,59,95,0.06)', color: '#163b5f', border: '1.5px solid rgba(22,59,95,0.2)', borderRadius: 9, fontSize: 13, fontWeight: 600, textDecoration: 'none' }}>
          <FeatherIcon icon="external-link" size={14} /> Open in New Tab
        </a>
        <button onClick={toggle} style={{ padding: '8px 16px', background: '#163b5f', color: '#fff', border: 'none', borderRadius: 9, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}>Close</button>
      </div>
    </Modal>
  );
};

const dzBase = {
  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 14px',
  border: '1.5px dashed rgba(22,59,95,0.25)', borderRadius: 10,
  background: 'rgba(22,59,95,0.04)', cursor: 'pointer',
  transition: 'border-color .15s, background .15s', fontSize: 12.5, color: '#163b5f',
};
const dzActiveStyle = { borderColor: '#163b5f', background: 'rgba(22,59,95,0.1)' };
const dzMulti = { flexDirection: 'column', justifyContent: 'center', textAlign: 'center', padding: '14px', gap: 5 };

const resolveUploadMeta = (field) => {
  const personMatch = field.match(/notification\.personsInvolved\[(\d+)\]\.attachments/);
  if (personMatch) return { docType: 'notification_person_attachments', personIndex: parseInt(personMatch[1], 10) };
  if (field === 'investigation.attachments') return { docType: 'investigation_attachments', personIndex: 0 };
  if (field === 'investigation.leaseDocument') return { docType: 'investigation_lease_document', personIndex: 0 };
  if (field === 'investigation.carInsuranceDocument') return { docType: 'investigation_car_insurance_document', personIndex: 0 };
  if (field === 'investigation.homeInsuranceDocument') return { docType: 'investigation_home_insurance_document', personIndex: 0 };
  return { docType: 'notification_person_attachments', personIndex: 0 };
};

const AttachmentsRow = ({ formik, reportId, signedUrlCache, setSignedUrlCache, setSelectedMedia, field, isRequired = false, docType = null, editable = true, onUploadingChange }) => {
  const [pendingUploads, setPendingUploads] = useState([]);
  const [promptState, setPromptState] = useState('ask');
  const adjustActive = useCallback((delta) => { onUploadingChange?.(delta); }, [onUploadingChange]);

  const getNested = (obj, path) => path.split('.').reduce((a, k) => {
    if (a == null) return undefined;
    const arrMatch = k.match(/^(\w+)\[(\d+)\]$/);
    if (arrMatch) return a[arrMatch[1]]?.[parseInt(arrMatch[2])];
    return a[k];
  }, obj);

  const attachments = getNested(formik.values, field) || (isRequired ? '' : []);
  const uploadedCount = isRequired ? (attachments ? 1 : 0) : (Array.isArray(attachments) ? attachments.length : 0);

  const processFiles = useCallback(async (files) => {
    if (!files?.length) return;
    if (!reportId) { toast.error('Save the report first before uploading files.'); return; }
    const valid = [], invalid = [];
    files.forEach((f) => {
      if (f.size > MAX_FILE_SIZE) invalid.push({ f, err: 'size' });
      else if (!isFileTypeAllowed(f.name, f.type)) invalid.push({ f, err: 'type' });
      else valid.push(f);
    });
    invalid.forEach(({ f, err }) => toast.error(err === 'size' ? `${f.name} exceeds 120 MB` : `${f.name}: this file type is not allowed for security reasons`));
    if (!valid.length) return;
    const pending = valid.map((f) => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file: f, preview: URL.createObjectURL(f),
      name: f.name, type: f.type, size: f.size, progress: 0, uploading: false,
    }));
    setPendingUploads((prev) => [...prev, ...pending]);
    pending.forEach((item) => uploadFile(item));
  }, [reportId, field, formik]);

  const { getRootProps, getInputProps, isDragActive, open: openDropzone } = useDropzone({
    onDrop: (acc, rej) => {
      rej.forEach(({ file, errors }) => errors.forEach((e) => {
        if (e.code === 'file-too-large') toast.error(`${file.name} is too large`);
        if (e.code === 'file-invalid-type') toast.error(`${file.name}: this file type is not allowed for security reasons`);
      }));
      processFiles(acc);
    },
    multiple: !isRequired,
    maxSize: MAX_FILE_SIZE, noClick: true, noKeyboard: true,
  });

  const uploadFile = async (item) => {
    const updById = (patch) => setPendingUploads((p) => p.map((u) => (u.id === item.id ? { ...u, ...patch } : u)));
    const removeById = () => setPendingUploads((p) => {
      const target = p.find((u) => u.id === item.id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return p.filter((u) => u.id !== item.id);
    });
    updById({ uploading: true });
    adjustActive(1);
    try {
      const { presignedUrl, key } = await requestIncidentUploadUrl(reportId, item.file.name, item.file.type);
      updById({ progress: 10 });
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', presignedUrl);
        xhr.setRequestHeader('Content-Type', item.file.type);
        xhr.upload.onprogress = (e) => { if (e.lengthComputable) updById({ progress: 10 + Math.round((e.loaded / e.total) * 80) }); };
        xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`S3 ${xhr.status}`));
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send(item.file);
      });
      updById({ progress: 100 });
      const { docType: resolvedDocType, personIndex } = resolveUploadMeta(field);
      await confirmIncidentUpload(reportId, key, resolvedDocType, personIndex);
      if (isRequired) {
        formik.setFieldValue(field, key);
      } else {
        const cur = getNested(formik.values, field) || [];
        if (!cur.includes(key)) formik.setFieldValue(field, [...cur, key]);
      }
      setPromptState('open');
      adjustActive(-1);
      setTimeout(removeById, 1500);
    } catch (err) { toast.error(err.message || `Failed to upload "${item.file.name}"`); adjustActive(-1); removeById(); }
  };

  const removePending = (id) => setPendingUploads((p) => {
    const item = p.find((u) => u.id === id);
    if (item?.preview) URL.revokeObjectURL(item.preview);
    return p.filter((u) => u.id !== id);
  });

  const removeUploaded = async (idx = null) => {
    const r = await Swal.fire({ title: 'Remove Attachment?', icon: 'warning', showCancelButton: true, confirmButtonColor: '#ef4444', cancelButtonColor: '#163b5f', confirmButtonText: 'Remove' });
    if (r.isConfirmed) {
      if (isRequired) {
        formik.setFieldValue(field, '');
      } else {
        const cur = [...(getNested(formik.values, field) || [])];
        cur.splice(idx, 1);
        formik.setFieldValue(field, cur);
        if (cur.length === 0) setPromptState('ask');
      }
      toast.success('Attachment removed');
    }
  };

  const previewUploaded = async (key) => {
    const name = fileNameFromKey(key);
    if (signedUrlCache[key]) {
      setSelectedMedia({ fileKey: key, signedUrl: signedUrlCache[key], name });
      return;
    }
    try {
      const urls = await getIncidentSignedUrls([key]);
      setSignedUrlCache((prev) => ({ ...prev, ...urls }));
      setSelectedMedia({ fileKey: key, signedUrl: urls[key], name });
    } catch (err) { toast.error(err.message || 'Failed to load preview'); }
  };

  const iconFor = (k) => isPdfKey(k) ? 'file-text' : isVideoKey(k) ? 'video' : 'image';

  const UploadedRow = ({ fileKey, onPreview, onRemove }) => {
    const isImg = isImageKey(fileKey);
    const thumbUrl = signedUrlCache?.[fileKey];
    return (
      <div onClick={onPreview} style={{
        display: 'flex', alignItems: 'center', gap: 10,
        padding: '8px 12px', borderRadius: 10, cursor: 'pointer',
        background: 'linear-gradient(135deg,rgba(22,59,95,0.05),rgba(58,107,152,0.04))',
        border: '1.5px solid rgba(22,59,95,0.15)',
        transition: 'border-color .15s, box-shadow .15s',
        marginBottom: 4,
      }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#163b5f'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(22,59,95,0.1)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(22,59,95,0.15)'; e.currentTarget.style.boxShadow = 'none'; }}
      >
        <div style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0, overflow: 'hidden', background: 'rgba(22,59,95,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {isImg && thumbUrl
            ? <img src={thumbUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            : <FeatherIcon icon={iconFor(fileKey)} size={15} color="#163b5f" />
          }
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#061524' }}>
            {fileNameFromKey(fileKey)}
          </div>
          <div style={{ fontSize: 10.5, color: '#163b5f', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
            <FeatherIcon icon="check-circle" size={10} />
            {isPdfKey(fileKey) ? 'Document' : isVideoKey(fileKey) ? 'Video' : 'Image'} · Uploaded
          </div>
        </div>
        {editable && (
          <button onClick={(e) => { e.stopPropagation(); onRemove(); }} style={{ width: 28, height: 28, borderRadius: 7, border: 'none', background: 'transparent', cursor: 'pointer', color: '#6a8ca8', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all .15s' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#fee2e2'; e.currentTarget.style.color = '#ef4444'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = '#6a8ca8'; }}
          >
            <FeatherIcon icon="trash-2" size={13} />
          </button>
        )}
      </div>
    );
  };

  const PendingRow = ({ m }) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', borderRadius: 10, border: '1.5px solid #dde3f0', background: '#fff', marginBottom: 4 }}>
      <div style={{ width: 36, height: 36, borderRadius: 8, flexShrink: 0, overflow: 'hidden', background: '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {m.type?.startsWith('image/') && m.preview
          ? <img src={m.preview} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 6 }} />
          : <FeatherIcon icon={m.type === 'application/pdf' ? 'file-text' : 'video'} size={14} color="#6a8ca8" />
        }
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: '#061524' }}>{m.name}</div>
        <div style={{ fontSize: 10.5, color: '#6a8ca8', marginTop: 2 }}>
          {(m.size / 1024 / 1024).toFixed(1)} MB{m.uploading && ` · ${m.progress}%`}
        </div>
        {m.uploading && (
          <div style={{ height: 3, background: '#dde3f0', borderRadius: 2, marginTop: 5, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${m.progress}%`, background: 'linear-gradient(90deg,#163b5f,#3a6b98)', borderRadius: 2, transition: 'width 0.3s' }} />
          </div>
        )}
      </div>
      {!m.uploading && editable && (
        <button onClick={(e) => { e.stopPropagation(); removePending(m.id); }} style={{ width: 26, height: 26, borderRadius: 6, border: 'none', background: 'transparent', cursor: 'pointer', color: '#6a8ca8', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <FeatherIcon icon="x" size={12} />
        </button>
      )}
    </div>
  );

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', marginBottom: 6 }}>
        {pendingUploads.map((m) => <PendingRow key={m.id} m={m} />)}
        {isRequired
          ? attachments && <UploadedRow fileKey={attachments} onPreview={() => previewUploaded(attachments)} onRemove={() => removeUploaded()} />
          : (attachments || []).map((key, idx) => <UploadedRow key={`u-${idx}`} fileKey={key} onPreview={() => previewUploaded(key)} onRemove={() => removeUploaded(idx)} />)
        }
      </div>

      {editable ? (
        isRequired ? (
          !attachments && (
            <div {...getRootProps()} onClick={openDropzone} style={{ ...dzBase, ...(isDragActive ? dzActiveStyle : {}) }}>
              <input {...getInputProps()} />
              <FeatherIcon icon="upload-cloud" size={15} color={isDragActive ? '#163b5f' : '#6a8ca8'} />
              <span>{isDragActive ? 'Drop here' : `Upload ${docType || 'file'} — JPG, PNG, PDF, MP4 · Max 120 MB`}</span>
            </div>
          )
        ) : (
          (() => {
            if (uploadedCount > 0 || pendingUploads.length > 0) {
              return (
                <div {...getRootProps()} onClick={openDropzone} style={{ ...dzBase, ...(isDragActive ? dzActiveStyle : {}), marginTop: 4 }}>
                  <input {...getInputProps()} />
                  <FeatherIcon icon="plus-circle" size={14} color={isDragActive ? '#163b5f' : '#6a8ca8'} />
                  <span>{isDragActive ? 'Drop to add' : 'Add more files'}</span>
                  <span style={{ marginLeft: 'auto', fontSize: 11, color: '#6a8ca8' }}>JPG · PNG · PDF · MP4 · Max 120 MB</span>
                </div>
              );
            }
            if (promptState === 'ask') return (
              <div style={{ textAlign: 'center', padding: '18px 16px', border: '1.5px dashed rgba(22,59,95,0.2)', borderRadius: 12, background: 'rgba(22,59,95,0.03)' }}>
                <div style={{ width: 38, height: 38, borderRadius: 10, background: 'linear-gradient(135deg,#163b5f,#3a6b98)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 10px', boxShadow: '0 3px 10px rgba(22,59,95,0.3)' }}>
                  <FeatherIcon icon="paperclip" size={17} color="#fff" />
                </div>
                <p style={{ fontSize: 13, margin: '0 0 4px', color: '#061524', fontWeight: 600 }}>Supporting Files</p>
                <p style={{ fontSize: 12, margin: '0 0 12px', color: '#6a8ca8' }}>Do you have any photos, videos, or documents?</p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
                  <BtnPrimary style={{ fontSize: 12, padding: '6px 14px' }} onClick={() => { setPromptState('open'); setTimeout(() => openDropzone(), 0); }}>
                    <FeatherIcon icon="upload" size={12} /> Yes — Add Files
                  </BtnPrimary>
                  <BtnGhost style={{ fontSize: 12, padding: '6px 14px' }} onClick={() => setPromptState('skip')}>No, skip</BtnGhost>
                </div>
              </div>
            );
            if (promptState === 'open') return (
              <div>
                <div {...getRootProps()} onClick={openDropzone} style={{ ...dzBase, ...dzMulti, ...(isDragActive ? dzActiveStyle : {}) }}>
                  <input {...getInputProps()} />
                  <FeatherIcon icon="upload-cloud" size={24} color={isDragActive ? '#163b5f' : '#6a8ca8'} />
                  <span style={{ fontWeight: 600, fontSize: 13, color: isDragActive ? '#163b5f' : '#061524' }}>
                    {isDragActive ? 'Drop files here' : 'Drag & drop or click to browse'}
                  </span>
                  <span style={{ fontSize: 11, color: '#6a8ca8' }}>JPG, PNG, GIF, WebP, MP4, MOV, PDF · Max 120 MB</span>
                </div>
                <button type="button" onClick={() => setPromptState('skip')} style={{ marginTop: 7, fontSize: 11, color: '#6a8ca8', background: 'none', border: 'none', padding: 0, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
                  <FeatherIcon icon="x" size={11} /> No files — skip
                </button>
              </div>
            );
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '9px 14px', border: '1.5px dashed rgba(22,59,95,0.15)', borderRadius: 10, background: 'rgba(22,59,95,0.03)', fontSize: 12.5, color: '#6a8ca8' }}>
                <FeatherIcon icon="paperclip" size={14} color="rgba(22,59,95,0.25)" />
                <span style={{ flex: 1 }}>No attachments — skipped</span>
                <BtnGhost style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => { setPromptState('open'); setTimeout(() => openDropzone(), 0); }}>
                  <FeatherIcon icon="upload-cloud" size={11} /> Add files
                </BtnGhost>
              </div>
            );
          })()
        )
      ) : (
        (isRequired ? !attachments : (attachments || []).length === 0) && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', color: '#6a8ca8', fontSize: 12.5, justifyContent: 'center', background: 'rgba(22,59,95,0.03)', borderRadius: 9, border: '1px dashed rgba(22,59,95,0.15)' }}>
            <FeatherIcon icon="file" size={13} color="#6a8ca8" />
            <span>No {isRequired ? `${docType} document` : 'attachments'} uploaded</span>
          </div>
        )
      )}
    </>
  );
};

function makeDefaultPerson() {
  return {
    type: '', specifyType: '', firstName: '', middleName: '', lastName: '',
    email: '', phoneNumbers: [''], unitNo: '', homeAddress: '',
    notificationToPropertyManagerDateTime: null,
    statement: '', attachments: [], witnesses: [],
    signature: { signature: '', date: null },
  };
}

const emptyRecipient = () => ({ firstName: '', lastName: '', email: '', managersNote: '' });

const AdditionalRecipientsModal = ({ isOpen, onConfirm, onCancel }) => {
  const [rows, setRows] = useState([emptyRecipient()]);
  const [errors, setErrors] = useState([{}]);

  useEffect(() => {
    if (isOpen) { setRows([emptyRecipient()]); setErrors([{}]); }
  }, [isOpen]);

  const update = (idx, field, value) => {
    setRows((prev) => prev.map((r, i) => (i === idx ? { ...r, [field]: value } : r)));
    setErrors((prev) => prev.map((e, i) => (i === idx ? { ...e, [field]: undefined } : e)));
  };
  const addRow = () => { setRows((p) => [...p, emptyRecipient()]); setErrors((p) => [...p, {}]); };
  const removeRow = (idx) => { setRows((p) => p.filter((_, i) => i !== idx)); setErrors((p) => p.filter((_, i) => i !== idx)); };

  const validate = () => {
    const newErrors = rows.map((r) => {
      const e = {};
      const hasData = r.firstName.trim() || r.lastName.trim() || r.email.trim();
      if (hasData || rows.length > 1) {
        if (!r.firstName.trim()) e.firstName = 'Required';
        if (!r.lastName.trim()) e.lastName = 'Required';
        if (!r.email.trim()) e.email = 'Required';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(r.email.trim())) e.email = 'Invalid email';
      }
      return e;
    });
    setErrors(newErrors);
    return newErrors.every((e) => Object.keys(e).length === 0);
  };

  const handleConfirm = () => {
    if (!validate()) return;
    onConfirm(rows.filter((r) => r.firstName.trim() || r.lastName.trim() || r.email.trim()).map((r) => ({
      firstName: r.firstName.trim(), lastName: r.lastName.trim(),
      email: r.email.trim(), managersNote: r.managersNote.trim(),
    })));
  };
  const handleSkip = () => onConfirm([]);

  if (!isOpen) return null;

  const ErrMsg = ({ msg }) => msg ? (
    <span style={{ fontSize: 11, color: '#ef4444', marginTop: 2, display: 'flex', alignItems: 'center', gap: 3 }}>
      <FeatherIcon icon="alert-circle" size={11} /> {msg}
    </span>
  ) : null;

  return (
    <Modal isOpen={isOpen} centered size="md" backdrop="static" className="pir-modal-wrap" style={{ zIndex: 10100 }}>
      <div className="pir-header" style={{ padding: '20px 26px 16px' }}>
        <div className="pir-header-glow" />
        <div className="pir-header-inner" style={{ padding: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 44, height: 44, borderRadius: 13, background: 'rgba(255,255,255,0.15)', border: '1.5px solid rgba(255,255,255,0.22)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 20 }}>📧</span>
            </div>
            <div>
              <h5 className="pir-header-title" style={{ fontSize: 16 }}>Notify Additional Recipients</h5>
              <p className="pir-header-sub" style={{ fontSize: 12 }}>
                Property managers are notified automatically. Add extra recipients or click <strong style={{ color: 'rgba(255,255,255,0.85)' }}>Skip</strong>.
              </p>
            </div>
          </div>
        </div>
      </div>

      <ModalBody style={{ padding: '20px 24px', maxHeight: '60vh', overflowY: 'auto', background: '#eaeff4' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {rows.map((row, idx) => (
            <div key={idx} className="pir-section-card" style={{ marginBottom: 0, padding: 16 }}>
              {rows.length > 1 && (
                <button onClick={() => removeRow(idx)} style={{ position: 'absolute', top: 12, right: 12, background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, padding: '2px 6px', borderRadius: 6 }}>
                  <FeatherIcon icon="trash-2" size={13} /> Remove
                </button>
              )}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 14px', marginBottom: 12 }}>
                <div>
                  <FL>First Name</FL>
                  <PirInput placeholder="First name" value={row.firstName}
                    style={{ borderColor: errors[idx]?.firstName ? '#ef4444' : undefined }}
                    onChange={(e) => update(idx, 'firstName', e.target.value)} />
                  <ErrMsg msg={errors[idx]?.firstName} />
                </div>
                <div>
                  <FL>Last Name</FL>
                  <PirInput placeholder="Last name" value={row.lastName}
                    style={{ borderColor: errors[idx]?.lastName ? '#ef4444' : undefined }}
                    onChange={(e) => update(idx, 'lastName', e.target.value)} />
                  <ErrMsg msg={errors[idx]?.lastName} />
                </div>
              </div>
              <div style={{ marginBottom: 12 }}>
                <FL>Email Address</FL>
                <PirInput type="email" placeholder="email@example.com" value={row.email}
                  style={{ borderColor: errors[idx]?.email ? '#ef4444' : undefined }}
                  onChange={(e) => update(idx, 'email', e.target.value)} />
                <ErrMsg msg={errors[idx]?.email} />
                <div style={{ marginTop: 12 }}>
                  <FL>Manager's Note (Optional)</FL>
                  <PirInput placeholder="Add a note for this recipient…" value={row.managersNote}
                    onChange={(e) => update(idx, 'managersNote', e.target.value)} />
                </div>
              </div>
            </div>
          ))}
        </div>

        <button onClick={addRow} className="pir-btn-ghost" style={{ marginTop: 12, width: '100%', justifyContent: 'center' }}>
          <FeatherIcon icon="plus" size={14} /> Add Another Recipient
        </button>
      </ModalBody>

      <ModalFooter style={{ padding: '14px 24px', gap: 10, borderTop: '1.5px solid rgba(22,59,95,0.1)', background: '#fff' }}>
        <BtnPrimary onClick={handleConfirm} style={{ padding: '10px 22px', fontSize: 14 }}>
          <FeatherIcon icon="send" size={15} /> Confirm & Submit
        </BtnPrimary>
        <BtnGhost onClick={handleSkip} style={{ padding: '10px 18px', fontSize: 14 }}>Skip</BtnGhost>
      </ModalFooter>
    </Modal>
  );
};

const PDFDownloadButton = ({ reportId, disabled }) => (
  <div className="pir-pdf-btn-wrap">
    <DownloadIncidentReportPDFButton reportId={reportId} disabled={disabled} />
  </div>
);

const DocumentField = ({ label, fieldKey, docType, inv, sf, attachProps }) => {
  const isNA = inv[fieldKey] === 'N/A';
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <FL>{label}</FL>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <PirPill active={isNA} onClick={() => sf(fieldKey, isNA ? '' : 'N/A')}>
            🚫 N/A
          </PirPill>
          {isNA && <span style={{ fontSize: 12, color: '#163b5f', fontWeight: 600 }}>Marked as N/A</span>}
        </div>
      </div>
      {!isNA && <AttachmentsRow {...attachProps} field={`investigation.${fieldKey}`} isRequired docType={docType} editable />}
    </div>
  );
};

const GenerateIncidentReportModal = ({
  isModalOpen, setIsModalOpen, filters, properties, formik,
  isEdit = false, onRefresh = () => { }, submitReport, initialComplete = false,
}) => {
  const isS1Locked = isEdit;

  const formPropertyId = formik.values?.propertyDetails?.propertyId;
  let activeProperty = null;
  if (formPropertyId) activeProperty = properties?.find((p) => p._id === formPropertyId);
  else if (filters?.property_id && !isEdit) activeProperty = properties?.find((p) => p._id === filters.property_id);
  const propertyTitle = activeProperty?.title || 'Property not selected';

  const [submitting, setSubmitting] = useState(false);
  const [signedUrlCache, setSignedUrlCache] = useState({});
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [uploadingCount, setUploadingCount] = useState(0);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);

  const uploadingCountRef = useRef(0);
  const reportId = formik.values?._id || null;
  // A complete report stays editable; the server records every change in its edit history.
  const isComplete = isEdit && initialComplete;

  const handleUploadingChange = useCallback((delta) => {
    uploadingCountRef.current = Math.max(0, uploadingCountRef.current + delta);
    setUploadingCount(uploadingCountRef.current);
  }, []);

  const isUploadingFiles = uploadingCount > 0;

  const fv = formik.values;
  const persons = fv.notification?.personsInvolved || [];
  const inv = fv.investigation || {};
  const invWitnesses = inv.witnesses || [];

  const sf = (sub, val) => formik.setFieldValue(`investigation.${sub}`, val);
  const addInvWitness = () => { sf('witnesses', [...invWitnesses, { firstName: '', middleName: '', lastName: '', address: '', phoneNo: '', email: '' }]); };

  const pf = (idx, sub) => `notification.personsInvolved[${idx}].${sub}`;
  const pv = (idx, sub) => {
    const p = persons[idx] || {};
    return sub.split('.').reduce((a, k) => a?.[k], p);
  };

  const toDateOnly = (v) => toLocalDateString(v);
  const fromDateOnly = (s) => {
    if (!s) return null;
    const [year, month, day] = s.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  useEffect(() => {
    if (!Array.isArray(persons) || persons.length === 0) {
      formik.setFieldValue('notification.personsInvolved', [makeDefaultPerson()]);
    }
  }, []);

  useEffect(() => {
    const allKeys = [];
    persons.forEach((p) => { (p?.attachments || []).forEach((k) => allKeys.push(k)); });
    (inv.attachments || []).forEach((k) => allKeys.push(k));
    if (inv.leaseDocument && inv.leaseDocument !== 'N/A') allKeys.push(inv.leaseDocument);
    if (inv.carInsuranceDocument && inv.carInsuranceDocument !== 'N/A') allKeys.push(inv.carInsuranceDocument);
    if (inv.homeInsuranceDocument && inv.homeInsuranceDocument !== 'N/A') allKeys.push(inv.homeInsuranceDocument);
    const missing = allKeys.filter((k) => k && k !== '__pending__' && !signedUrlCache[k]);
    if (missing.length) {
      getIncidentSignedUrls(missing).then((urls) => setSignedUrlCache((p) => ({ ...p, ...urls }))).catch(() => { });
    }
  }, [fv]);

  const addPerson = () => { formik.setFieldValue('notification.personsInvolved', [...persons, makeDefaultPerson()]); };
  const removePerson = (idx) => {
    if (persons.length <= 1) { toast.info('At least one person is required.'); return; }
    const c = [...persons]; c.splice(idx, 1);
    formik.setFieldValue('notification.personsInvolved', c);
  };

  const handleSubmit = async () => {
    if (isUploadingFiles) { toast.info('Please wait for uploads to complete.'); return; }
    const isValid = await validateIncidentReport(formik.values);
    if (!isValid) return;
    setShowPreviewModal(true);
  };

  const finishSubmit = async (additionalEmails) => {
    setSubmitting(true);
    try {
      if (isEdit) {
        const currentNotificationEmails = fv.notificationEmails || [];
        const merged = [...currentNotificationEmails];
        for (const extra of additionalEmails) {
          const exists = merged.some((e) => e.email?.toLowerCase() === extra.email?.toLowerCase());
          if (!exists) merged.push(extra);
        }
        formik.setFieldValue('notificationEmails', merged);
        await new Promise((r) => setTimeout(r, 0));
        const valuesWithEmails = { ...formik.values, notificationEmails: merged };
        const savedReportId = await submitReport(valuesWithEmails);
        if (savedReportId) {
          await sendIncidentNotification(savedReportId, 'updated', additionalEmails).catch((err) => console.warn('Update notification failed:', err));
        }
        setIsModalOpen(false);
        onRefresh();
      } else {
        const savedReportId = await submitReport(formik.values);
        if (savedReportId) {
          await sendIncidentNotification(savedReportId, 'created', []).catch((err) => console.warn('Create notification failed:', err));
        }
        setIsModalOpen(false);
        onRefresh();
      }
    } catch (err) {
      console.error('Failed to save incident report:', err);
      toast.error(err.message || 'Failed to save report');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRecipientsConfirm = async (additionalEmails) => {
    setShowRecipientsModal(false);
    await finishSubmit(additionalEmails);
  };

  const attachProps = { formik, reportId, signedUrlCache, setSignedUrlCache, setSelectedMedia, onUploadingChange: handleUploadingChange };
  const isSubmitDisabled = submitting || formik.isSubmitting || isUploadingFiles;

  return (
    <>
      <Modal
        isOpen={isModalOpen}
        toggle={undefined}
        backdrop="static"
        keyboard={false}
        className="modal-xl modal-fullscreen pir-modal-wrap"
        scrollable
        style={{ fontFamily: "'DM Sans','Inter',-apple-system,sans-serif" }}
      >
        <div className="pir-header" style={{ borderRadius: 0, padding: '26px 32px 22px' }}>
          <div className="pir-header-glow" />
          <div className="pir-header-inner" style={{ padding: 0, maxWidth: "unset" }}>
            <div className="pir-header-top" style={{ marginBottom: 18 }}>
              <div>
                <h1 className="pir-header-title" style={{ fontSize: 22, textShadow: '0 2px 20px rgba(37,99,235,0.4)' }}>
                  {isEdit ? 'Edit Incident Report' : 'Generate Incident Report'}
                </h1>
                <p className="pir-header-sub" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {propertyTitle ? `Property: ${propertyTitle}` : 'Complete all sections accurately'}
                </p>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <img src="/images/rentdigicare-logo.svg" alt="RentDigiCare" className="pir-header-logo" style={{ height: 32 }} />
                <button onClick={() => setIsModalOpen(false)} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', cursor: 'pointer', padding: 8, borderRadius: 10, transition: 'all 0.2s' }}>
                  <FeatherIcon icon="x" size={18} />
                </button>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '2fr 2fr 1.5fr', gap: 12, marginBottom: 16 }}>
              {[
                { label: 'Manager Reporting', key: 'managerReporting', ph: 'Full name', type: 'text', required: true },
                { label: 'Manager Email', key: 'managerEmail', ph: 'manager@example.com', type: 'email' },
                { label: 'Manager Phone', key: 'managerPhoneNo', ph: 'Phone number', type: 'text' },
              ].map(({ label, key, ph, type, required }) => (
                <div key={key}>
                  <label style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.65)', marginBottom: 5, display: 'block' }}>
                    {label}{required && <span style={{ color: '#fca5a5' }}> *</span>}
                  </label>
                  <PirInput
                    type={type} placeholder={ph}
                    value={key === 'managerPhoneNo' ? (fv.notification?.managerPhoneNo || fv.notification?.managerphoneNo || '') : (fv.notification?.[key] || '')}
                    onChange={(e) => formik.setFieldValue(`notification.${key}`, e.target.value)}
                    style={{ background: 'rgba(255,255,255,0.12)', border: '1.5px solid rgba(255,255,255,0.2)', color: '#fff' }}
                  />
                </div>
              ))}
            </div>

            <div className="pir-badge-row" style={{ padding: 0 }}>
              {isUploadingFiles && (
                <span className="pir-badge pir-badge-amber">
                  <FeatherIcon icon="upload-cloud" size={11} /> Uploading files…
                </span>
              )}
              {isComplete && (
                <span className="pir-badge pir-badge-green">
                  <FeatherIcon icon="check-circle" size={11} /> Report Complete
                </span>
              )}
            </div>
          </div>
        </div>

        <ModalBody style={{ padding: '22px 24px', background: '#eaeff4', overflowY: 'auto', maxHeight: 'calc(90vh - 200px)' }}>

          {/* Section 1 — Persons Involved */}
          <SectionCard number="1" title="Persons Involved" subtitle="Information about each person involved in the incident">
            {isS1Locked && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', marginBottom: 14, background: 'linear-gradient(135deg,#fffbeb,#fef3c7)', border: '1px solid rgba(217,119,6,0.3)', borderRadius: 10, fontSize: 12.5, color: '#92400e' }}>
                <FeatherIcon icon="lock" size={13} color="#b45309" />
                <span><span className='text-black'>Edit mode:</span> Person details are locked and cannot be modified.</span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <span style={{ fontSize: 12.5, color: '#6a8ca8', fontWeight: 500 }}>
                {persons.length} person{persons.length !== 1 ? 's' : ''} listed
              </span>
              {!isS1Locked && (
                <BtnPrimary onClick={addPerson}>
                  <FeatherIcon icon="plus" size={14} /> Add Person
                </BtnPrimary>
              )}
            </div>

            {persons.map((p, idx) => (
              <div key={idx} className="pir-person-card" style={{ marginBottom: 14 }}>
                <div className="pir-section-header" style={{ padding: '12px 18px', borderBottom: '1.5px solid rgba(22,59,95,0.08)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 28, height: 28, borderRadius: 8, background: 'linear-gradient(135deg,#163b5f,#3a6b98)', color: '#fff', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(22,59,95,0.3)' }}>{idx + 1}</div>
                    <span style={{ fontSize: 13.5, fontWeight: 700, color: '#fff' }}>
                      {[p.firstName, p.lastName].filter(Boolean).join(' ') || `Person ${idx + 1}`}
                    </span>
                    {p.type && (
                      <span style={{ fontSize: 10.5, fontWeight: 700, color: 'rgba(255,255,255,0.85)', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', padding: '2px 9px', borderRadius: 20, textTransform: 'uppercase', letterSpacing: '0.4px' }}>
                        {p.type === 'other' && p.specifyType ? `Other (${p.specifyType})` : p.type}
                      </span>
                    )}
                  </div>
                  {persons.length > 1 && !isS1Locked && (
                    <BtnDangerSm onClick={() => removePerson(idx)}>
                      <FeatherIcon icon="trash-2" size={13} /> Remove
                    </BtnDangerSm>
                  )}
                </div>

                <div className="pir-section-body" style={{ padding: '18px 20px', position: 'relative', zIndex: 1 }}>

                  {/* Personal Info subsection */}
                  <InvSubSection icon="user" label="Personal Information">
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr 1.5fr 1.5fr 1.5fr', gap: 12, marginBottom: 12 }}>
                      <div>
                        <FL required>Person Type</FL>
                        <Select
                          options={PERSON_TYPE_OPTIONS}
                          value={PERSON_TYPE_OPTIONS.find((o) => o.value === p.type) || null}
                          onChange={(opt) => {
                            formik.setFieldValue(pf(idx, 'type'), opt?.value || '');
                            if (opt?.value !== 'other') formik.setFieldValue(pf(idx, 'specifyType'), '');
                          }}
                          isDisabled={isS1Locked}
                          placeholder="Select…"
                          menuPortalTarget={document.body} menuPosition="fixed"
                          styles={dtpSelectStyles(isS1Locked)}
                          isSearchable={false}
                        />
                        {p.type === 'other' && (
                          <div style={{ marginTop: 8 }}>
                            <FL required>Specify Type</FL>
                            <PirInput placeholder="e.g. Visitor, Guest…" value={p.specifyType || ''}
                              onChange={(e) => formik.setFieldValue(pf(idx, 'specifyType'), e.target.value)}
                              disabled={isS1Locked} />
                          </div>
                        )}
                      </div>
                      <div><FL required>First Name</FL><PirInput placeholder="First name" value={p.firstName || ''} onChange={(e) => formik.setFieldValue(pf(idx, 'firstName'), e.target.value)} disabled={isS1Locked} /></div>
                      <div><FL>Middle Name</FL><PirInput placeholder="Middle name" value={p.middleName || ''} onChange={(e) => formik.setFieldValue(pf(idx, 'middleName'), e.target.value)} disabled={isS1Locked} /></div>
                      <div><FL required>Last Name</FL><PirInput placeholder="Last name" value={p.lastName || ''} onChange={(e) => formik.setFieldValue(pf(idx, 'lastName'), e.target.value)} disabled={isS1Locked} /></div>
                      <div><FL>Email</FL><PirInput type="email" placeholder="email@example.com" value={p.email || ''} onChange={(e) => formik.setFieldValue(pf(idx, 'email'), e.target.value)} disabled /></div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1.4fr 4fr 3fr', gap: 12 }}>
                      <div>
                        <FL>Phone Number(s)</FL>
                        {(p.phoneNumbers && p.phoneNumbers.length > 0 ? p.phoneNumbers : ['']).map((phone, phIdx) => (
                          <div key={phIdx} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: phIdx < (p.phoneNumbers || ['']).length - 1 ? 6 : 0 }}>
                            <PirInput placeholder="(000) 000-0000" value={phone || ''}
                              onChange={(e) => {
                                const updated = [...(p.phoneNumbers || [''])];
                                updated[phIdx] = e.target.value;
                                formik.setFieldValue(pf(idx, 'phoneNumbers'), updated);
                              }}
                              disabled={isS1Locked}
                              style={{ flex: 1 }} />
                            {!isS1Locked && (
                              <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                                {(p.phoneNumbers || ['']).length > 1 && (
                                  <button type="button" onClick={() => {
                                    const updated = [...(p.phoneNumbers || [''])];
                                    updated.splice(phIdx, 1);
                                    formik.setFieldValue(pf(idx, 'phoneNumbers'), updated.length > 0 ? updated : ['']);
                                  }} style={{ width: 28, height: 28, borderRadius: 8, border: '1.5px solid rgba(239,68,68,0.3)', background: 'rgba(239,68,68,0.08)', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, transition: 'all 0.15s', lineHeight: 1, padding: 0 }} title="Remove phone number">−</button>
                                )}
                                {phIdx === (p.phoneNumbers || ['']).length - 1 && (
                                  <button type="button" onClick={() => {
                                    const updated = [...(p.phoneNumbers || [''])];
                                    updated.push('');
                                    formik.setFieldValue(pf(idx, 'phoneNumbers'), updated);
                                  }} style={{ width: 28, height: 28, borderRadius: 8, border: '1.5px solid rgba(22,59,95,0.25)', background: 'rgba(22,59,95,0.06)', color: '#163b5f', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 700, transition: 'all 0.15s', lineHeight: 1, padding: 0 }} title="Add phone number">+</button>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      <div>
                        <FL>Unit No.</FL>
                        <div style={{ display: 'flex', gap: 7, alignItems: 'center' }}>
                          <div style={{ flex: 1 }}>
                            <PirInput placeholder="#204" value={p.unitNo === 'N/A' ? '' : p.unitNo || ''}
                              onChange={(e) => formik.setFieldValue(pf(idx, 'unitNo'), e.target.value)}
                              disabled={isS1Locked || p.unitNo === 'N/A'} />
                          </div>
                          <PirPill active={p.unitNo === 'N/A'} disabled={isS1Locked} onClick={() => { if (!isS1Locked) formik.setFieldValue(pf(idx, 'unitNo'), p.unitNo === 'N/A' ? '' : 'N/A'); }}>🚫 N/A</PirPill>
                        </div>
                      </div>
                      <div>
                        <FL>Home Address</FL>
                        <AddressInput placeholder="Full home address" value={p.homeAddress || ''}
                          onChange={(val) => formik.setFieldValue(pf(idx, 'homeAddress'), val)}
                          disabled={isS1Locked} />
                      </div>
                      <div>
                        <FL hint="When was the manager notified?">Notification Date & Time</FL>
                        <DateTimePicker value={p.notificationToPropertyManagerDateTime}
                          onChange={(val) => formik.setFieldValue(pf(idx, 'notificationToPropertyManagerDateTime'), val)}
                          disabled={isS1Locked} maxNow />
                      </div>
                    </div>
                  </InvSubSection>

                  {/* Statement subsection */}
                  <InvSubSection icon="file-text" label="Person's Statement">
                    <FL>Include who, what, when, where, why and how — provide as much detail as possible</FL>
                    <PirTextarea rows={3} placeholder="Person's account of the incident…"
                      value={p.statement || ''}
                      onChange={(e) => formik.setFieldValue(pf(idx, 'statement'), e.target.value)}
                      disabled={isS1Locked} />
                  </InvSubSection>

                  {/* Supporting Attachments subsection */}
                  <InvSubSection icon="paperclip" label="Supporting Attachments">
                    <AttachmentsRow {...attachProps} field={`notification.personsInvolved[${idx}].attachments`} editable={!isS1Locked} />
                  </InvSubSection>

                  {/* Witnesses subsection */}
                  <InvSubSection icon="users" label="Witnesses">
                    <WitnessGate
                      choiceField={`notification.personsInvolved[${idx}].witnessChoice`}
                      witnessesField={`notification.personsInvolved[${idx}].witnesses`}
                      formik={formik}
                      editable={!isS1Locked}
                      onAdd={() => {
                        const cur = p.witnesses || [];
                        formik.setFieldValue(pf(idx, 'witnesses'), [...cur, { firstName: '', middleName: '', lastName: '', address: '', phoneNo: '', email: '' }]);
                      }}
                    />

                    {((p.witnessChoice === 'yes' || (p.witnesses || []).length > 0)) && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                          {!isS1Locked && (
                            <BtnGhost style={{ padding: '6px 14px' }} onClick={() => {
                              const cur = p.witnesses || [];
                              formik.setFieldValue(pf(idx, 'witnesses'), [...cur, { firstName: '', middleName: '', lastName: '', address: '', phoneNo: '', email: '' }]);
                            }}>
                              <FeatherIcon icon="plus" size={14} /> Add Witness
                            </BtnGhost>
                          )}
                        </div>
                        {(p.witnesses || []).map((w, wIdx) => (
                          <div key={wIdx}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 5 }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: '#163b5f' }}>Witness {wIdx + 1}</span>
                              {!isS1Locked && (
                                <BtnDangerSm onClick={() => {
                                  const cur = [...(p.witnesses || [])];
                                  cur.splice(wIdx, 1);
                                  formik.setFieldValue(pf(idx, 'witnesses'), cur);
                                  if (cur.length === 0) formik.setFieldValue(`notification.personsInvolved[${idx}].witnessChoice`, null);
                                }}>
                                  <FeatherIcon icon="trash-2" size={12} />
                                </BtnDangerSm>
                              )}
                            </div>
                            <WitnessBlock w={w} idx={wIdx} fieldPrefix={`notification.personsInvolved[${idx}].witnesses`} disabled={isS1Locked} formik={formik} />
                          </div>
                        ))}
                      </div>
                    )}
                  </InvSubSection>

                  {/* Signature subsection */}
                  <InvSubSection icon="edit-3" label="Digital Signature">
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <div style={{ maxWidth: 480, width: '100%' }}>
                        <SignaturePad
                          value={pv(idx, 'signature.signature') || ''}
                          date={pv(idx, 'signature.date') ? toLocalDateString(pv(idx, 'signature.date')) : ''}
                          onChange={(data) => formik.setFieldValue(pf(idx, 'signature.signature'), data)}
                          onDateChange={(dateStr) => formik.setFieldValue(pf(idx, 'signature.date'), dateStr)}
                          disabled={isS1Locked}
                        />
                      </div>
                    </div>
                  </InvSubSection>

                </div>
              </div>
            ))}
          </SectionCard>

          {/* Section 2 — Investigation */}
          <SectionCard number="2" title="Investigation" subtitle="Incident details, environmental conditions and follow-up">

            {/* Date & Time */}
            <InvSubSection icon="calendar" label="Date & Time">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <FL required>Occurrence Date & Time</FL>
                  <DateTimePicker value={inv.occurrenceDateTime} onChange={(val) => sf('occurrenceDateTime', val)} maxNow />
                </div>
                <div>
                  <FL>Notification to Property Manager Date & Time</FL>
                  <DateTimePicker value={inv.notificationToPropertyManagerDateTime} onChange={(val) => sf('notificationToPropertyManagerDateTime', val)} maxNow />
                </div>
              </div>
            </InvSubSection>

            {/* Environmental Conditions */}
            <InvSubSection icon="cloud" label="Environmental Conditions">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12 }}>
                <div>
                  <FL>Weather Conditions</FL>
                  <Select classNamePrefix="react-select" isMulti options={WEATHER_OPTIONS}
                    value={arrToOpts(inv.weatherConditions)}
                    onChange={(opts) => sf('weatherConditions', optsToArr(opts))}
                    placeholder="Select…" closeMenuOnSelect={false}
                    menuPortalTarget={document.body} menuPosition="fixed" styles={{ ...pirSelectStyles, ...selectMenuPortalStyles }} />
                  {(inv.weatherConditions || []).includes('Other') && (
                    <PirInput placeholder="Specify weather" value={inv.weatherConditionsOther || ''}
                      onChange={(e) => sf('weatherConditionsOther', e.target.value)}
                      style={{ marginTop: 6 }} />
                  )}
                </div>
                <div>
                  <FL>Temperature (°C)</FL>
                  <PirInput type="number" step="0.1" placeholder="°C" value={inv.temperatureCelsius ?? ''}
                    onChange={(e) => sf('temperatureCelsius', e.target.value ? parseFloat(e.target.value) : null)} />
                </div>
                <div>
                  <FL>Environment Conditions</FL>
                  <Select classNamePrefix="react-select" isMulti options={ENVIRONMENT_OPTIONS}
                    value={arrToOpts(inv.environmentConditions)}
                    onChange={(opts) => sf('environmentConditions', optsToArr(opts))}
                    placeholder="Select…" closeMenuOnSelect={false}
                    menuPortalTarget={document.body} menuPosition="fixed" styles={{ ...pirSelectStyles, ...selectMenuPortalStyles }} />
                </div>
                <div>
                  <FL>Visibility</FL>
                  <Select classNamePrefix="react-select" isMulti options={VISIBILITY_OPTIONS}
                    value={arrToOpts(inv.visibility)}
                    onChange={(opts) => sf('visibility', optsToArr(opts))}
                    placeholder="Select…" closeMenuOnSelect={false}
                    menuPortalTarget={document.body} menuPosition="fixed" styles={{ ...pirSelectStyles, ...selectMenuPortalStyles }} />
                </div>
              </div>
            </InvSubSection>

            {/* Investigation Narrative */}
            <InvSubSection icon="file-text" label="Investigation Narrative">
              <FL required>Facts / Time / Observation of Resident / Full Story / Follow-up Actions</FL>
              <PirTextarea rows={5} placeholder="Provide a detailed investigation narrative…"
                value={inv.statement || ''}
                onChange={(e) => sf('statement', e.target.value)} />
            </InvSubSection>

            {/* Witnesses */}
            <InvSubSection icon="users" label="Witnesses">
              <WitnessGate
                choiceField="investigation.witnessChoice"
                witnessesField="investigation.witnesses"
                formik={formik}
                editable
                onAdd={addInvWitness}
              />

              {(inv.witnessChoice === 'yes' || invWitnesses.length > 0) && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 6 }}>
                    <BtnGhost style={{ padding: '6px 14px' }} onClick={addInvWitness}>
                      <FeatherIcon icon="plus" size={14} /> Add Witness
                    </BtnGhost>
                  </div>
                  {invWitnesses.map((w, wIdx) => (
                    <div key={wIdx}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <p style={{ margin: 0, fontSize: 12.5, fontWeight: 700, color: '#163b5f', display: 'flex', alignItems: 'center', gap: 5 }}>
                          <FeatherIcon icon="user" size={13} color="#3a6b98" /> Witness {wIdx + 1}
                        </p>
                        <BtnDangerSm onClick={() => {
                          const c = [...invWitnesses];
                          c.splice(wIdx, 1);
                          sf('witnesses', c);
                          if (c.length === 0) formik.setFieldValue('investigation.witnessChoice', null);
                        }}>
                          <FeatherIcon icon="trash-2" size={13} /> Remove
                        </BtnDangerSm>
                      </div>
                      <WitnessBlock w={w} idx={wIdx} fieldPrefix="investigation.witnesses" formik={formik} />
                    </div>
                  ))}
                </div>
              )}
            </InvSubSection>

            {/* Supporting Documents */}
            <InvSubSection icon="folder" label="Supporting Documents">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14 }}>
                <DocumentField label="Lease Document" fieldKey="leaseDocument" docType="Lease Document" inv={inv} sf={sf} attachProps={attachProps} />
                <DocumentField label="Car Insurance Document" fieldKey="carInsuranceDocument" docType="Car Insurance" inv={inv} sf={sf} attachProps={attachProps} />
                <DocumentField label="Home Insurance Document" fieldKey="homeInsuranceDocument" docType="Home Insurance" inv={inv} sf={sf} attachProps={attachProps} />
              </div>
            </InvSubSection>

            {/* Additional Attachments */}
            <InvSubSection icon="paperclip" label="Additional Attachments">
              <AttachmentsRow {...attachProps} field="investigation.attachments" editable />
            </InvSubSection>

            {/* Investigator */}
            <InvSubSection icon="check-circle" label="Investigation Completed By">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                <div>
                  <FL>Investigator Name</FL>
                  <PirInput placeholder="Full name" value={inv.investigationDoneBy?.name || ''}
                    onChange={(e) => sf('investigationDoneBy.name', e.target.value)} />
                </div>
                <div>
                  <FL>Investigation Date</FL>
                  <PirInput type="date" value={toDateOnly(inv.investigationDoneBy?.date)}
                    max={todayLocalString()}
                    onChange={(e) => sf('investigationDoneBy.date', fromDateOnly(e.target.value))} />
                </div>
              </div>
            </InvSubSection>

          </SectionCard>

          {/* Section 3 — Follow-up (global, not per person) */}
          <SectionCard number="3" title="Follow-up & Resolution" subtitle="Specify if this report requires further action">
            <div style={{
              display: 'flex', alignItems: 'center', gap: 14,
              padding: '16px 20px', borderRadius: 14,
              background: fv.followUpRequired ? 'linear-gradient(135deg,rgba(217,119,6,0.08),rgba(251,191,36,0.05))' : 'rgba(22,59,95,0.03)',
              border: `1.5px solid ${fv.followUpRequired ? 'rgba(217,119,6,0.25)' : 'rgba(22,59,95,0.1)'}`,
              transition: 'all 0.3s ease'
            }}>
              <div style={{
                width: 48, height: 48, borderRadius: 12,
                background: fv.followUpRequired ? 'linear-gradient(135deg,#f59e0b,#fbbf24)' : 'rgba(22,59,95,0.08)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: fv.followUpRequired ? '0 4px 12px rgba(217,119,6,0.25)' : 'none',
                transition: 'all 0.3s ease'
              }}>
                <FeatherIcon icon="flag" size={20} color={fv.followUpRequired ? '#fff' : '#6a8ca8'} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#163b5f', marginBottom: 2 }}>
                  Follow-up Required? <span style={{ color: '#ef4444' }}>*</span>
                </div>
                <div style={{ fontSize: 12, color: '#6a8ca8' }}>Mark this report if it needs further review or action by management</div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {[{ val: true, label: 'Yes', icon: 'check-circle' }, { val: false, label: 'No', icon: 'circle' }].map(({ val, label, icon }) => {
                  const isSelected = fv.followUpRequired === val;
                  return (
                    <label key={label} style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '8px 20px', borderRadius: 11, cursor: 'pointer',
                      fontSize: 13.5, fontWeight: 700, userSelect: 'none',
                      border: `2px solid ${isSelected ? (val ? '#f59e0b' : '#163b5f') : 'rgba(22,59,95,0.15)'}`,
                      background: isSelected ? (val ? 'linear-gradient(135deg,#f59e0b,#fbbf24)' : 'linear-gradient(135deg,#163b5f,#3a6b98)') : '#fff',
                      color: isSelected ? '#fff' : '#6a8ca8',
                      boxShadow: isSelected ? `0 4px 14px ${val ? 'rgba(217,119,6,0.25)' : 'rgba(22,59,95,0.2)'}` : 'none',
                      transition: 'all 0.2s',
                    }}>
                      <input
                        type="radio"
                        name="followUpRequired"
                        checked={isSelected}
                        onChange={() => formik.setFieldValue('followUpRequired', val)}
                        style={{ display: 'none' }}
                      />
                      <FeatherIcon icon={isSelected ? icon : 'circle'} size={14} />
                      {label}
                    </label>
                  );
                })}
              </div>
            </div>
          </SectionCard>

          {isEdit && (
            <SectionCard number="4" title="Change History" subtitle="Every saved change, newest first">
              <IncidentReportHistory statusHistory={fv.statusHistory} editHistory={fv.editHistory} />
            </SectionCard>
          )}

        </ModalBody>

        <ModalFooter style={{ background: '#fff', borderTop: '1.5px solid rgba(22,59,95,0.1)', padding: '16px 28px', gap: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            {fv?._id && <PDFDownloadButton reportId={fv._id} disabled={submitting || formik.isSubmitting || isUploadingFiles} />}
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {isComplete && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#059669', fontWeight: 600 }}>
                <FeatherIcon icon="check-circle" size={14} />
                This report is complete — changes are recorded in its history.
              </span>
            )}
            <button
              onClick={handleSubmit}
              disabled={isSubmitDisabled}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 9,
                padding: '11px 28px',
                background: isSubmitDisabled ? 'linear-gradient(135deg,#6ee7b7,#a7f3d0)' : 'linear-gradient(135deg,#059669,#10b981)',
                color: '#fff', border: 'none', borderRadius: 11,
                fontSize: 14.5, fontWeight: 700, cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
                fontFamily: "'DM Sans','Inter',-apple-system,sans-serif",
                boxShadow: isSubmitDisabled ? 'none' : '0 4px 18px rgba(5,150,105,0.4)',
                transition: 'all 0.2s',
                opacity: isSubmitDisabled ? 0.8 : 1,
              }}
            >
              {submitting || formik.isSubmitting
                ? <><Spinner size="sm" color="light" style={{ marginRight: 6 }} /> Saving…</>
                : isUploadingFiles
                  ? <><Spinner size="sm" color="light" style={{ marginRight: 6 }} /> Uploading…</>
                  : <><FeatherIcon icon="send" size={15} /> {isEdit ? 'Save Incident Report' : 'Submit Incident Report'}</>
              }
            </button>
            <BtnGhost
              onClick={() => setIsModalOpen(false)}
              disabled={submitting || formik.isSubmitting || isUploadingFiles}
              style={{ padding: '11px 22px', fontSize: 14 }}
            >
              Cancel
            </BtnGhost>
          </div>
        </ModalFooter>
      </Modal>

      <AdditionalRecipientsModal isOpen={showRecipientsModal} onConfirm={handleRecipientsConfirm} onCancel={() => setShowRecipientsModal(false)} />
      <IncidentReportPreview
        isOpen={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        reportData={formik.values}
        onSubmit={() => {
          setShowPreviewModal(false);
          setShowRecipientsModal(true);
        }}
        onCancel={() => setShowPreviewModal(false)}
        userRole="admin"
      />

      <FilePreviewModal
        isOpen={!!selectedMedia}
        toggle={() => setSelectedMedia(null)}
        fileKey={selectedMedia?.fileKey}
        signedUrl={selectedMedia?.signedUrl}
        name={selectedMedia?.name}
      />
    </>
  );
};

const AddressInput = ({ value, onChange, disabled, placeholder = 'Search address…' }) => {
  const wrapperRef = useRef(null);

  const getDropdownStyle = () => {
    if (!wrapperRef.current) return {};
    const rect = wrapperRef.current.getBoundingClientRect();
    return {
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 100000,
      background: '#fff',
      border: '1.5px solid rgba(22,59,95,0.15)',
      borderRadius: 12,
      boxShadow: '0 12px 40px rgba(6,21,36,0.25)',
      maxHeight: 250,
      overflowY: 'auto'
    };
  };

  if (disabled) return <PirInput placeholder={placeholder} value={value || ''} disabled />;

  return (
    <PlacesAutocomplete value={value || ''} onChange={onChange} onSelect={onChange}
      searchOptions={{ componentRestrictions: { country: ['US', 'CA'] } }}>
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div ref={wrapperRef} style={{ position: 'relative', width: '100%' }}>
          <PirInput {...getInputProps({ placeholder, style: { width: '100%' } })} />
          {(loading || suggestions.length > 0) && ReactDOM.createPortal(
            <div style={getDropdownStyle()}>
              {loading && <div style={{ padding: '10px 14px', fontSize: 13, color: '#6a8ca8' }}>Loading suggestions…</div>}
              {suggestions.map((s) => (
                <div {...getSuggestionItemProps(s)} key={s.placeId} style={{
                  padding: '11px 14px',
                  fontSize: 13.5,
                  cursor: 'pointer',
                  background: s.active ? 'rgba(22,59,95,0.1)' : '#fff',
                  color: s.active ? '#163b5f' : '#061524',
                  borderBottom: '1px solid rgba(22,59,95,0.07)',
                  transition: 'background 0.1s'
                }}>
                  {s.description}
                </div>
              ))}
            </div>,
            document.body
          )}
        </div>
      )}
    </PlacesAutocomplete>
  );
};

export { GenerateIncidentReportModal };